import { supabase } from '../lib/supabase';
import type { UserCalendarReminder } from '../types/objectivesTasks';

import { createNotification } from './objectivesTasksService';

export type CreateCalendarReminderPayload = {
  title: string;
  note?: string | null;
  entry_date: string;
  reminder_date: string;
};

/** Fetch reminders for a user, optionally filtered by entry date range (YYYY-MM-DD). */
export async function getCalendarReminders(
  userId: string,
  options?: { entryFrom?: string; entryTo?: string }
): Promise<UserCalendarReminder[]> {
  let q = supabase
    .from('user_calendar_reminders')
    .select('*')
    .eq('user_id', userId)
    .order('entry_date', { ascending: true })
    .order('created_at', { ascending: true });
  if (options?.entryFrom) q = q.gte('entry_date', options.entryFrom);
  if (options?.entryTo) q = q.lte('entry_date', options.entryTo);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as UserCalendarReminder[];
}

export async function createCalendarReminder(
  userId: string,
  payload: CreateCalendarReminderPayload
): Promise<UserCalendarReminder> {
  const { data, error } = await supabase
    .from('user_calendar_reminders')
    .insert({
      user_id: userId,
      title: payload.title.trim(),
      note: payload.note?.trim() || null,
      entry_date: payload.entry_date,
      reminder_date: payload.reminder_date,
    })
    .select()
    .single();
  if (error) throw error;
  return data as UserCalendarReminder;
}

export async function deleteCalendarReminder(userId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('user_calendar_reminders')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throw error;
}

/** Create in-app notifications for reminders that are due and not yet notified. Call on layout load so user sees reminders in notification tray after login. */
export async function syncCalendarReminderNotifications(userId: string): Promise<void> {
  const today = new Date().toISOString().slice(0, 10);
  const { data: dueReminders } = await supabase
    .from('user_calendar_reminders')
    .select('id, title, note, reminder_date')
    .eq('user_id', userId)
    .lte('reminder_date', today)
    .is('notification_created_at', null);
  const list = (dueReminders ?? []) as { id: string; title: string; note: string | null; reminder_date: string }[];
  if (list.length === 0) return;

  const { data: existing } = await supabase
    .from('user_notifications')
    .select('entity_id')
    .eq('user_id', userId)
    .eq('entity_type', 'calendar_reminder')
    .eq('type', 'calendar_reminder_due');
  const notifiedIds = new Set((existing ?? []).map((r) => r.entity_id).filter(Boolean));

  for (const r of list) {
    if (notifiedIds.has(r.id)) continue;
    const message = r.note ? `${r.title}: ${r.note}` : r.title;
    try {
      await createNotification(userId, {
        type: 'calendar_reminder_due',
        entity_type: 'calendar_reminder',
        entity_id: r.id,
        message,
      });
      await supabase
        .from('user_calendar_reminders')
        .update({ notification_created_at: new Date().toISOString() })
        .eq('id', r.id)
        .eq('user_id', userId);
    } catch {
      // skip on conflict or transient error
    }
    notifiedIds.add(r.id);
  }
}
